import { inject, Injectable, signal } from '@angular/core';
import { map, Observable, of, tap } from 'rxjs';

import { AccountAccessStatus, AccountMember } from '@celum/authentication';
import { DataUtil } from '@celum/core/util';
import { Notification, NotificationWithMembers } from '@celum/shared/domain';
import { AccountMemberService } from '@celum/shared/shared';

@Injectable({ providedIn: 'root' })
export class NotificationUserService {
  private accountMemberService = inject(AccountMemberService);
  private cachedMembers = signal<Map<string, AccountMember>>(new Map());

  public attachMemberInfo(notifications: Notification[]): Observable<NotificationWithMembers[]> {
    const userIds: string[] = [];
    notifications.forEach(notification => {
      if (notification.data.affectedUserIds) {
        userIds.push(...notification.data.affectedUserIds);
      }
      userIds.push(notification.senderId);
    });

    return this.load([...new Set(userIds)]).pipe(
      map(foundUsersMap => {
        return notifications.map(notification => {
          const affectedUsersMap = new Map(notification.data.affectedUserIds.map(id => [id, foundUsersMap.get(id)]));
          const sender = foundUsersMap.get(notification.senderId);
          return { ...notification, affectedUsersMap, sender };
        });
      })
    );
  }

  private load(ids: string[]): Observable<Map<string, AccountMember>> {
    const idsNotCachedYet = [...new Set(ids)].filter(id => !this.cachedMembers().has(id));

    if (DataUtil.isEmpty(idsNotCachedYet)) {
      return of(new Map(ids.map(id => [id, this.cachedMembers().get(id)])));
    }

    return this.accountMemberService.findMembers({ ids: idsNotCachedYet, membershipStatuses: [AccountAccessStatus.ACTIVE, AccountAccessStatus.INACTIVE] }).pipe(
      map(result => result.entities),
      tap(entities => this.cacheMembers(entities, idsNotCachedYet)),
      map(() => new Map(ids.map(id => [id, this.cachedMembers().get(id)])))
    );
  }

  private cacheMembers(newMembers: AccountMember[], idsNotCachedYet: string[]): void {
    const members = new Map(this.cachedMembers());
    idsNotCachedYet.forEach(id => members.set(id, newMembers.find(member => member.oid === id || member.id === id) ?? null));
    this.cachedMembers.set(members);
  }
}
